import type { FxType } from "../../../src/lib/audio";

export const FX_TYPES: FxType[] = [
  "distortion",
  "saturation",
  "bitcrush",
  "delay",
  "chorus",
  "flanger",
  "phaser",
  "filter",
  "reverb",
  "colorReverb",
  "compressor",
  "limiter",
  "eq",
  "utility",
];

export const FX_TYPE_LABELS: Record<FxType, string> = {
  distortion: "distortion",
  saturation: "saturation",
  bitcrush: "bitcrush",
  delay: "delay",
  chorus: "chorus",
  flanger: "flanger",
  phaser: "phaser",
  filter: "filter",
  reverb: "reverb",
  colorReverb: "color reverb",
  compressor: "compressor",
  limiter: "limiter",
  eq: "EQ",
  utility: "utility",
};

export const FX_PARAM_LABELS: Record<FxType, Record<string, string>> = {
  distortion: { drive: "Drive" },
  saturation: { drive: "Drive", tone: "Tone", mix: "Mix" },
  bitcrush: { bits: "Bits", downsample: "Downsample" },
  delay: { timeMs: "Time ms", feedback: "Feedback" },
  chorus: { rateHz: "Rate Hz", depthMs: "Depth ms", delayMs: "Delay ms" },
  flanger: { rateHz: "Rate Hz", depthMs: "Depth ms", delayMs: "Delay ms", feedback: "Feedback" },
  phaser: {
    rateHz: "Rate Hz",
    depth: "Depth",
    feedback: "Feedback",
    stages: "Stages",
    centerHz: "Center Hz",
  },
  filter: {
    mode: "Mode",
    cutoffHz: "Cutoff Hz",
    resonance: "Resonance",
    drive: "Drive",
  },
  reverb: { room: "Room", damp: "Damp" },
  colorReverb: {
    preGain: "Pre gain",
    gain: "Gain",
    feedback: "Feedback",
    damping: "Damping",
    ducking: "Ducking",
    width: "Width",
    predelayMix: "Predelay",
    saturateWet: "Sat wet",
    satDrive: "Sat drive",
  },
  compressor: {
    thresholdDb: "Threshold dB",
    ratio: "Ratio",
    attackMs: "Attack ms",
    releaseMs: "Release ms",
    kneeDb: "Knee dB",
    makeupDb: "Makeup dB",
    detectorHpfHz: "Det HPF Hz",
  },
  limiter: {
    ceilingDb: "Ceiling dB",
    releaseMs: "Release ms",
    makeupDb: "Makeup dB",
  },
  eq: {
    lowDb: "80 Hz",
    lowMidDb: "250 Hz",
    midDb: "1 kHz",
    highMidDb: "4 kHz",
    highDb: "10 kHz",
  },
  utility: {
    gainDb: "Gain dB",
    pan: "Pan",
    widthPct: "Width %",
    mono: "Mono",
    invertL: "Invert L",
    invertR: "Invert R",
  },
};

/** Params rendered as checkboxes (0/1) instead of range sliders. */
export const FX_PARAM_TOGGLES = new Set<string>(["mono", "invertL", "invertR"]);

/** Discrete numeric params rendered as selects (value stays a number in params). */
export const FX_PARAM_OPTIONS: Partial<
  Record<FxType, Record<string, Array<{ value: number; label: string }>>>
> = {
  filter: {
    mode: [
      { value: 0, label: "LPF" },
      { value: 1, label: "HPF" },
      { value: 2, label: "BPF" },
      { value: 3, label: "Notch" },
      { value: 4, label: "Ladder" },
    ],
  },
};

export const FX_PARAM_RANGES: Record<string, { min: number; max: number; step: number }> = {
  drive: { min: 1, max: 40, step: 0.5 },
  bits: { min: 1, max: 16, step: 1 },
  downsample: { min: 1, max: 32, step: 1 },
  timeMs: { min: 1, max: 1500, step: 1 },
  feedback: { min: 0, max: 0.98, step: 0.01 },
  rateHz: { min: 0.05, max: 8, step: 0.05 },
  depthMs: { min: 0, max: 12, step: 0.1 },
  delayMs: { min: 0.5, max: 30, step: 0.1 },
  depth: { min: 0, max: 1, step: 0.01 },
  stages: { min: 2, max: 12, step: 1 },
  centerHz: { min: 100, max: 8000, step: 10 },
  mode: { min: 0, max: 4, step: 1 },
  cutoffHz: { min: 40, max: 18000, step: 1 },
  resonance: { min: 0, max: 1, step: 0.01 },
  tone: { min: 0, max: 1, step: 0.01 },
  mix: { min: 0, max: 1, step: 0.01 },
  room: { min: 0, max: 0.95, step: 0.01 },
  damp: { min: 0, max: 1, step: 0.01 },
  preGain: { min: 0, max: 2, step: 0.01 },
  gain: { min: 0, max: 2, step: 0.01 },
  damping: { min: 0, max: 0.99, step: 0.01 },
  ducking: { min: 0, max: 1, step: 0.01 },
  width: { min: 0, max: 1, step: 0.01 },
  predelayMix: { min: 0, max: 1, step: 0.01 },
  saturateWet: { min: 0, max: 1, step: 0.01 },
  satDrive: { min: 1, max: 12, step: 0.1 },
  thresholdDb: { min: -60, max: 0, step: 0.5 },
  ceilingDb: { min: -24, max: 0, step: 0.1 },
  ratio: { min: 1, max: 20, step: 0.1 },
  attackMs: { min: 0.1, max: 200, step: 0.1 },
  releaseMs: { min: 1, max: 2000, step: 1 },
  kneeDb: { min: 0, max: 24, step: 0.5 },
  makeupDb: { min: -12, max: 24, step: 0.5 },
  detectorHpfHz: { min: 0, max: 400, step: 1 },
  lowDb: { min: -18, max: 18, step: 0.5 },
  lowMidDb: { min: -18, max: 18, step: 0.5 },
  midDb: { min: -18, max: 18, step: 0.5 },
  highMidDb: { min: -18, max: 18, step: 0.5 },
  highDb: { min: -18, max: 18, step: 0.5 },
  gainDb: { min: -24, max: 24, step: 0.5 },
  pan: { min: -100, max: 100, step: 1 },
  widthPct: { min: 0, max: 200, step: 1 },
  mono: { min: 0, max: 1, step: 1 },
  invertL: { min: 0, max: 1, step: 1 },
  invertR: { min: 0, max: 1, step: 1 },
};

export function defaultWetForFxType(type: FxType): number {
  if (type === "colorReverb") {
    return 0.14;
  }
  if (
    type === "compressor"
    || type === "eq"
    || type === "utility"
    || type === "filter"
    || type === "limiter"
    || type === "saturation"
  ) {
    return 1;
  }
  return 0.35;
}

export function formatFxParam(value: number): string {
  if (Number.isInteger(value) || Math.abs(value) >= 10) {
    return String(Math.round(value * 100) / 100);
  }
  return value.toFixed(2);
}
